import React, { useState } from 'react';
import { ShieldCheck, AlertTriangle, CheckCircle, Clock, Info } from 'lucide-react';
import { IdCardUpload } from '@/components/ui/IdCardUpload';

type VerificationStatus = 'not_verified' | 'pending' | 'verified' | 'rejected';

interface VerificationState {
  status: VerificationStatus;
  frontImage: File | null;
  backImage: File | null;
}

const Verification = () => {
  const [state, setState] = useState<VerificationState>({
    status: 'not_verified',
    frontImage: null,
    backImage: null,
  });

  const handleFrontImageChange = (file: File | null) => {
    setState((prev) => ({ ...prev, frontImage: file }));
  };

  const handleBackImageChange = (file: File | null) => {
    setState((prev) => ({ ...prev, backImage: file }));
  };

  const handleSubmit = () => {
    if (state.frontImage && state.backImage) {
      setState((prev) => ({ ...prev, status: 'pending' }));
      // console.log('Submitting verification...', state);
    }
  };

  const canSubmit = state.frontImage && state.backImage && state.status === 'not_verified';

  const getStatusBadge = () => {
    switch (state.status) {
      case 'verified':
        return (
          <div className="flex items-center gap-2 !px-3 !py-1.5 !bg-status-success/10 !rounded-lg">
            <CheckCircle className="w-4 h-4 !text-status-success" />
            <span className="!text-sm !font-medium !text-status-success">Đã xác minh</span>
          </div>
        );
      case 'pending':
        return (
          <div className="flex items-center gap-2 !px-3 !py-1.5 !bg-status-warning/10 !rounded-lg">
            <Clock className="w-4 h-4 !text-status-warning" />
            <span className="!text-sm !font-medium !text-status-warning">Đang chờ xác minh</span>
          </div>
        );
      case 'rejected':
        return (
          <div className="flex items-center gap-2 !px-3 !py-1.5 !bg-status-error/10 !rounded-lg">
            <AlertTriangle className="w-4 h-4 !text-status-error" />
            <span className="!text-sm !font-medium !text-status-error">Xác minh thất bại</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-2 !px-3 !py-1.5 !bg-gray-100 dark:!bg-dark-border !rounded-lg">
            <ShieldCheck className="w-4 h-4 !text-gray-500 dark:!text-dark-text-muted" />
            <span className="!text-sm !font-medium !text-gray-500 dark:!text-dark-text-muted">Chưa xác minh</span>
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      <div className="!bg-white dark:!bg-dark-card !rounded-xl !p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="!text-lg !font-bold !text-gray-900 dark:!text-white">
              Xác minh danh tính
            </h2>
            <p className="!text-sm !text-gray-500 dark:!text-dark-text-secondary mt-1">
              Xác minh danh tính để mở khóa đầy đủ tính năng
            </p>
          </div>
          {getStatusBadge()}
        </div>
      </div>

      <div className="!bg-primary/5 dark:!bg-primary/10 !border !border-primary/20 !rounded-xl !p-4">
        <div className="flex gap-3">
          <Info className="w-5 h-5 !text-primary flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="!text-sm !font-medium !text-gray-900 dark:!text-white mb-2">
              Hướng dẫn chụp ảnh CCCD/CMND
            </h3>
            <ul className="!text-sm !text-gray-600 dark:!text-dark-text-secondary space-y-1">
              <li>• Đảm bảo ảnh rõ nét, không bị mờ hoặc lóa</li>
              <li>• Chụp toàn bộ mặt trước và mặt sau của CCCD/CMND</li>
              <li>• Không che khuất bất kỳ thông tin nào trên giấy tờ</li>
              <li>• Sử dụng ảnh gốc, không chỉnh sửa</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="!bg-white dark:!bg-dark-card !rounded-xl !p-6">
        <h3 className="!text-base !font-semibold !text-gray-900 dark:!text-white mb-6">
          Tải lên ảnh Căn cước công dân / Chứng minh nhân dân
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <IdCardUpload
            label="Mặt trước CCCD/CMND"
            description="Chụp rõ ràng mặt có ảnh chân dung"
            onFileChange={handleFrontImageChange}
          />
          <IdCardUpload
            label="Mặt sau CCCD/CMND"
            description="Chụp rõ ràng mặt có mã QR"
            onFileChange={handleBackImageChange}
          />
        </div>

        <div className="mt-8 flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`!px-6 !py-2.5 !rounded-lg !text-sm !font-medium !transition-colors !border-none ${
              canSubmit
                ? '!bg-primary hover:!bg-primary-dark !text-white'
                : '!bg-gray-200 dark:!bg-dark-border !text-gray-400 dark:!text-dark-text-muted cursor-not-allowed'
            }`}
          >
            Gửi xác minh
          </button>
        </div>
      </div>

      {state.status === 'pending' && (
        <div className="!bg-status-warning/10 !border !border-status-warning/20 !rounded-xl !p-4">
          <div className="flex gap-3">
            <Clock className="w-5 h-5 !text-status-warning flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="!text-sm !font-medium !text-status-warning mb-1">
                Đang chờ xác minh
              </h3>
              <p className="!text-sm !text-gray-600 dark:!text-dark-text-secondary">
                Yêu cầu xác minh của bạn đang được xử lý. Quá trình này có thể mất từ 1-3 ngày làm việc.
              </p>
            </div>
          </div>
        </div>
      )}

      {state.status === 'verified' && (
        <div className="!bg-status-success/10 !border !border-status-success/20 !rounded-xl !p-4">
          <div className="flex gap-3">
            <CheckCircle className="w-5 h-5 !text-status-success flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="!text-sm !font-medium !text-status-success mb-1">
                Xác minh thành công
              </h3>
              <p className="!text-sm !text-gray-600 dark:!text-dark-text-secondary">
                Tài khoản của bạn đã được xác minh. Bạn có thể sử dụng đầy đủ các tính năng.
              </p>
            </div>
          </div>
        </div>
      )}

      {state.status === 'rejected' && (
        <div className="!bg-status-error/10 !border !border-status-error/20 !rounded-xl !p-4">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 !text-status-error flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="!text-sm !font-medium !text-status-error mb-1">
                Xác minh thất bại
              </h3>
              <p className="!text-sm !text-gray-600 dark:!text-dark-text-secondary">
                Ảnh CCCD/CMND không hợp lệ. Vui lòng tải lên ảnh rõ ràng hơn và thử lại.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Verification;